// Phase 14 — Settings → Rates admin panel.
//
// CRUD over the OfficialRate table (GET/POST/PATCH/DELETE /api/official-rates).
// Four cards, one per rate kind, each landing under the "Rates" section
// header (order 70) via its own order:71–74. Admin-only — SettingsView only
// mounts this behind {_meAuthIsAdmin && …}; the buttons additionally
// disable when !isAdmin as defence in depth.
//
// Each card's headline is HOW FAR ITS TABLE REACHES — c1-rate-coverage-checkpoint,
// brief 30 §3, security-register item 65.
//
// It used to be a "Last updated" stamp computed as max(updatedAt) over the
// rows, which answers WHEN DID ANYONE LAST WRITE A ROW to a reader asking HOW
// FAR DOES THIS TABLE REACH. Those are different questions and the difference
// is the item: on staging today every kind reports 28/05/2026 — one seed run,
// never touched since — and the commercial rates stopped covering anything on
// 1 July. The panel said "Last updated 28/05/2026" and looked maintained. This
// is the only place in the application where a human asks the question at all,
// so it now answers the one they are asking; the stamp survives, demoted, as
// the secondary fact it always was.
//
// The coverage comes from GET /api/official-rates/summary, which runs the SAME
// derivation as the `rate-coverage` roster checkpoint (server/src/lib/rateCoverage.js
// → the interest engine's buildRatePeriods). Deliberately not computed here: a
// second copy of the half-year date maths would agree with the engine right up
// until the day it mattered, which is the day this card exists for.
//
// Editing is inline, saved on blur (the SettingsTaxonomies house pattern).
//
// RateKindCard is defined at module scope (stable identity) so it is not
// remounted on every SettingsView re-render — see CLAUDE.md "Known patterns".

import { useState, useEffect, useCallback } from "react";
import type { CSSProperties, Dispatch } from "react";
import { Trash2, Plus } from "lucide-react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import { usePermissions } from "../hooks/usePermissions";
import { FS } from "../lib/tokens";
import { fmtBEDate } from "../lib/interestEngine";
import type { Translate } from "../lib/interestRefusal";
import { useConfirm } from "./useConfirmAndUndo";
import Table from "./Table";
import AmountField from "./AmountField"; // 208 §10 — the one amount field (a fee schedule's euros)
import DateField from "./DateField"; // 208 §10 — « in force since », the app's date field
import { amountValue } from "../lib/amountText";

// One kind's verdict as GET /api/official-rates/summary returns it — the shape
// server/src/lib/rateCoverage.js produces, declared where it is consumed.
// `state` is the whole contract: "pass" carries a coverageEnd and a runway,
// "fail" carries a shape (ran-out | hole | runway) and how far behind it is,
// "not-determined" carries only a reason.
type KindCoverage = {
  kind: string;
  state: "pass" | "fail" | "not-determined";
  shape?: "ran-out" | "hole" | "runway";
  rows: number;
  periods: number;
  reason?: string;
  newestTo?: string;
  coverageEnd?: string;
  runwayDays?: number;
  runwayFloor?: number;
  lastCovered?: string;
  uncoveredDays?: number;
};

// Per-kind meta-field schema. Universal columns (code/label/value/sortOrder)
// are handled generically; these are the kind-specific meta.* fields.
// 208 §10 — typed, so a card reads its kind's fields as what they are (`money`
// among them) rather than as `any`.
interface MetaField {
  name: string;
  type: "int" | "float" | "text" | "date";
}
interface RateKindDef {
  kind: string;
  order: number;
  labelKey: string;
  fallback: string;
  valueLabel: string;
  /** 208 §10 — its value and its bands are euros: the one amount field. */
  money?: boolean;
  metaFields: MetaField[];
}
const RATE_KINDS: RateKindDef[] = [
  {
    kind: "legal_interest",
    order: 71,
    labelKey: "settings.rates.legalInterest",
    fallback: "Legal interest",
    valueLabel: "rate %",
    metaFields: [{ name: "year", type: "int" }],
  },
  {
    kind: "commercial_interest",
    order: 72,
    labelKey: "settings.rates.commercialInterest",
    fallback: "Commercial interest",
    valueLabel: "rate %",
    metaFields: [
      { name: "year", type: "int" },
      { name: "half", type: "int" },
    ],
  },
  {
    kind: "listing_fee",
    order: 73,
    labelKey: "settings.rates.listingFees",
    fallback: "Listing fees",
    valueLabel: "fee €",
    money: true, // 208 §10 — its value and its bands are euros: the one amount field
    metaFields: [
      { name: "tribunalType", type: "text" },
      { name: "claimMin", type: "float" },
      { name: "claimMax", type: "float" },
      // BRIEF 87 §3a — the vintage and the authority, on the `meta` convention
      // the interest kinds already use (`meta.year`, `meta.half`) rather than a
      // parallel one. Not a schema change: OfficialRate.meta is a free Json?.
      { name: "inForceSince", type: "date" },
      { name: "source", type: "text" },
    ],
  },
  {
    kind: "procedural_compensation",
    order: 74,
    labelKey: "settings.rates.proceduralCompensation",
    fallback: "Procedural compensation",
    valueLabel: "basic €",
    money: true, // 208 §10 — see listing_fee
    metaFields: [
      { name: "claimMin", type: "float" },
      { name: "claimMax", type: "float" },
      { name: "increased", type: "float" },
      { name: "decreased", type: "float" },
      // §3a — see listing_fee above.
      { name: "inForceSince", type: "date" },
      { name: "source", type: "text" },
    ],
  },
];

const inputStyle = (w?: any): CSSProperties => ({
  padding: "2px 6px",
  fontSize: FS.s11,
  ...(w ? { width: w } : {}),
});

/* 208 §10 — A ROW'S EUROS AND ITS DATE, TYPED THE APP'S WAY AND SAVED WHEN LEFT
   (the panel's own rule: a row saves on blur). A fee schedule's value and its
   bands go through the one amount field (« 1 250,00 », a decimal comma);
   « in force since » is the app's date field (DD/MM/YY at rest, the ISO day
   stored) — it was a number box (207C C5). Each cell holds what is typed
   until it is left; the caller keys it by the stored value, so a reload
   re-seeds it. Module scope: a stable identity (Bug A). */
function RateAmountCell({ value, onSave, disabled, width }: Readonly<{ value: unknown; onSave: Dispatch<number | null>; disabled?: boolean; width?: number }>) {
  const [v, setV] = useState<number | null>(() => amountValue(value));
  return <AmountField value={v} empty="" inputMode="decimal" disabled={disabled} style={inputStyle(width)} onAmount={setV} onLeave={onSave} />;
}
function RateDateCell({ value, onSave, disabled }: Readonly<{ value: unknown; onSave: Dispatch<string>; disabled?: boolean }>) {
  const [v, setV] = useState<string>(() => (typeof value === "string" ? value : ""));
  return (
    <DateField
      value={v}
      disabled={disabled}
      style={inputStyle(110)}
      onChange={(iso) => {
        setV(iso);
      }}
      onBlur={() => {
        onSave(v);
      }}
    />
  );
}

// Coerce a raw input string to the field's declared type. Empty → undefined
// (so the key is dropped from meta rather than stored as '').
function coerceMeta(type: any, raw: any) {
  if (raw === "" || raw == null) return undefined;
  if (type === "int") {
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : undefined;
  }
  if (type === "float") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  // §3a — an ISO day, stored as the string the <input type="date"> produced.
  // Rejected rather than coerced if it is not one: a vintage nobody can compare
  // is worse than an absent vintage, because it looks answered.
  if (type === "date") {
    const v = String(raw).trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined;
  }
  return String(raw);
}

function buildMeta(metaFields: any, draft: any) {
  const meta: Record<string, any> = {};
  for (const f of metaFields) {
    const v = coerceMeta(f.type, draft[f.name]);
    if (v !== undefined) meta[f.name] = v;
  }
  return Object.keys(meta).length ? meta : null;
}

// The coverage headline. `cov` is one entry of GET /summary's `coverage` —
// the server's verdict, rendered, never recomputed. Three states, and the
// third is honest rather than absent: `listing_fee` and
// `procedural_compensation` are fee schedules, not dated series, and the
// engine derives no period from them. Saying so beats a blank.
// BRIEF 87 §3a — A STALE REFERENCE TABLE IS SHOWN WITH A WARNING, NEVER REFUSED
// [RULED]. `listing_fee` and `procedural_compensation` are Belgian reference
// tables — 65a corrected: NOTHING computes from them (sectionRegistry.js's
// "no code consumer at all"; rateCoverage derives zero periods on every run), so
// a stale one is not a money defect. It is a human reading a wrong number off a
// Settings table, which is real and is what this warns about.
//
// The vintage is read off the ROWS, not asserted anywhere: the newest
// `meta.inForceSince` in the table is the table's edition. Three states, and the
// first is today's on every box —
//
//   UNDATED   no row carries inForceSince. The table's vintage is unrecorded,
//             so nothing can be said about whether it is current. Saying THAT is
//             the honest answer and it is also the prompt to fill it in; a
//             silent table reads as a fresh one.
//   STALE     the newest edition is over STALE_AFTER_MONTHS old. Warned, with
//             the date and the source, and still rendered — refusing would hide
//             the only copy of the numbers anybody has.
//   CURRENT   dated within the window; the date and source are shown plainly.
//
// The threshold is a JUDGEMENT and is written down as one rather than buried:
// both tables are indexed instruments revised every few years, so two years
// without a revision is worth a second look and is not by itself an error. The
// warning says "check", not "wrong".
const STALE_AFTER_MONTHS = 24;

type EditionRow = { meta?: { inForceSince?: unknown; source?: string | null } | null };
function newestEdition(rows: EditionRow[]) {
  let best: string | null = null;
  let source: string | null = null;
  for (const r of rows || []) {
    const d = r && r.meta && r.meta.inForceSince;
    if (typeof d !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(d)) continue;
    if (best == null || d > best) {
      best = d;
      source = (r.meta && r.meta.source) || null;
    }
  }
  return { date: best, source };
}

function referenceTableLine(rows: EditionRow[], t: Translate) {
  const { date, source } = newestEdition(rows);
  if (!date) {
    return {
      tone: "var(--am)",
      text: t("settings.rates.edition.undated", {
        fallback:
          "⚠ vintage unrecorded — add \u201cin force since\u201d and the source so a reader can tell whether these figures are current",
      }),
    };
  }
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - STALE_AFTER_MONTHS);
  const stale = new Date(date) < cutoff;
  const src = source ? ` \u00b7 ${source}` : "";
  return stale
    ? {
        tone: "var(--am)",
        text:
          t("settings.rates.edition.stale", {
            date: fmtBEDate(date),
            fallback:
              "\u26a0 in force since {date} \u2014 over two years old; check for a newer edition before relying on these figures",
          }) + src,
      }
    : {
        tone: "var(--tm)",
        text:
          t("settings.rates.edition.current", {
            date: fmtBEDate(date),
            fallback: "in force since {date}",
          }) + src,
      };
}

function coverageLine(cov: KindCoverage | null | undefined, t: Translate, rows?: EditionRow[]) {
  if (!cov) return null;
  if (cov.state === "not-determined") {
    // §3a — an undated kind is where the two reference tables land, and "no
    // coverage to report" was true and unhelpful. The EDITION is what a reader
    // of a reference table needs, so it replaces the sentence rather than
    // sitting beside it.
    return referenceTableLine(rows || [], t);
  }
  if (cov.state === "pass") {
    return {
      tone: "var(--tm)",
      text: t("settings.rates.coverage.covers", {
        date: fmtBEDate(cov.coverageEnd),
        days: cov.runwayDays,
        count: cov.runwayDays, // 208 §20
        fallback: "covers today — up to {date}, {days} day(s) left",
      }),
    };
  }
  if (cov.shape === "hole") {
    return {
      tone: "var(--am)",
      text: t("settings.rates.coverage.hole", {
        date: fmtBEDate(cov.newestTo),
        fallback:
          "⚠ today is NOT covered — a rate row is missing in the middle (the table reaches {date})",
      }),
    };
  }
  return {
    tone: "var(--am)",
    text: t("settings.rates.coverage.ranOut", {
      date: fmtBEDate(cov.lastCovered),
      days: cov.uncoveredDays,
      count: cov.uncoveredDays, // 208 §20
      fallback:
        "⚠ RAN OUT — {date} is the last covered day; {days} day(s) since have no rate at all",
    }),
  };
}

type RateKindCardProps = {
  def: RateKindDef;
  rows: any[];
  coverage?: KindCoverage | null;
  isAdmin?: boolean;
  toast?: (msg: string) => void;
  onChanged: () => void;
  t: any;
};

function RateKindCard({ def, rows, coverage, isAdmin, toast, onChanged, t }: RateKindCardProps) {
  const askConfirm = useConfirm();
  const [collapsed, setCollapsed] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<{ code: string; label: string; value: string; meta: Record<string, any> }>({
    code: "",
    label: "",
    value: "",
    meta: {},
  });

  const resetDraft = () =>
    setDraft({ code: "", label: "", value: "", meta: {} });

  // Kept, and kept SECOND. max(updatedAt) is a real fact about the rows; it is
  // simply not the answer to "is this table still good", which is what the
  // headline above it now says.
  const lastUpdated = rows.reduce((acc, r) => {
    const ts = r.updatedAt ? +new Date(r.updatedAt) : 0;
    return ts > acc ? ts : acc;
  }, 0);
  const cov = coverageLine(coverage, t, rows);

  async function patchRow(id: any, patch: any) {
    try {
      await api.patch("/official-rates/" + id, patch);
      onChanged();
    } catch (e: any) {
      toast &&
        toast(
          t("settings.rates.saveFailed", { fallback: "Save failed" }) +
            ": " +
            (e.message || ""),
        );
    }
  }

  async function create() {
    const code = draft.code.trim();
    const label = draft.label.trim();
    const value = Number(draft.value);
    if (!code || !label || !Number.isFinite(value)) {
      toast &&
        toast(
          t("settings.rates.fieldsRequired", {
            fallback: "code, label and a numeric value are required",
          }),
        );
      return;
    }
    try {
      await api.post("/official-rates", {
        kind: def.kind,
        code,
        label,
        value,
        sortOrder: rows.length,
        meta: buildMeta(def.metaFields, draft.meta),
      });
      resetDraft();
      setAdding(false);
      onChanged();
    } catch (e: any) {
      toast &&
        toast(
          t("settings.rates.createFailed", { fallback: "Create failed" }) +
            ": " +
            (e.message || ""),
        );
    }
  }

  async function remove(row: any) {
    // 157 §2 — the two strings the native dialog joined with a blank line
    // become the dialog's title and its body. Neither is reworded.
    askConfirm({
      title: t("settings.rates.confirmDelete", {
        fallback: "Delete this rate row?",
      }),
      message: row.label,
      confirmLabel: t("common.delete", { fallback: "Delete" }),
      onConfirm: async () => {
        try {
          await api.del("/official-rates/" + row.id);
          onChanged();
        } catch (e: any) {
          toast &&
            toast(
              t("settings.rates.deleteFailed", { fallback: "Delete failed" }) +
                ": " +
                (e.message || ""),
            );
        }
      },
    });
  }

  const title = t(def.labelKey, { fallback: def.fallback });

  return (
    <div
      className={`detail-card settings-pane${collapsed ? " cl" : ""}`}
      style={{ marginBottom: 10, order: def.order }}
    >
      <h4
        className="settings-pane-title"
        style={{ margin: "0 0 6px" }}
        onClick={() => setCollapsed((v) => !v)}
      >
        <span className="settings-chevron">{collapsed ? "▶" : "▼"}</span>
        {title}
        <span
          style={{
            fontSize: FS.s9,
            color: "var(--tm)",
            fontWeight: 400,
            marginLeft: 8,
          }}
        >
          {t("settings.rates.entryCount", {
            count: rows.length,
            fallback: `${rows.length} entr${rows.length === 1 ? "y" : "ies"}`,
          })}
          {cov && (
            <span style={{ color: cov.tone, fontWeight: cov.tone === "var(--am)" ? 600 : 400 }}>
              {" · " + cov.text}
            </span>
          )}
          {lastUpdated > 0 &&
            " · " +
              t("settings.rates.lastUpdated", { fallback: "Last updated" }) +
              ": " +
              new Date(lastUpdated).toLocaleDateString()}
        </span>
      </h4>
      <div style={{ overflowX: "auto" }}>
        <Table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: FS.s11 }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid var(--br)" }}>
              <Th>{t("common.code", { fallback: "Code" })}</Th>
              <Th>{t("settings.rates.col.label", { fallback: "Label" })}</Th>
              <Th>{def.valueLabel}</Th>
              {def.metaFields.map((f) => (
                <Th key={f.name}>{f.name}</Th>
              ))}
              <Th style={{ textAlign: "center", width: 60 }}>
                {t("common.order", { fallback: "Order" })}
              </Th>
              <Th style={{ width: 28 }} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={5 + def.metaFields.length}
                  style={{
                    padding: "8px 6px",
                    color: "var(--tm)",
                    fontSize: FS.s11,
                  }}
                >
                  {t("settings.rates.empty", { fallback: "No entries yet." })}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} style={{ borderBottom: "1px solid var(--br)" }}>
                <td style={{ padding: "3px 6px" }}>
                  <input
                    className="field-input"
                    style={inputStyle(110)}
                    defaultValue={r.code}
                    disabled={!isAdmin}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== r.code) patchRow(r.id, { code: v });
                    }}
                  />
                </td>
                <td style={{ padding: "3px 6px" }}>
                  <input
                    className="field-input"
                    style={{ ...inputStyle(), width: "100%" }}
                    defaultValue={r.label}
                    disabled={!isAdmin}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== r.label) patchRow(r.id, { label: v });
                    }}
                  />
                </td>
                <td style={{ padding: "3px 6px" }}>
                  {def.money ? (
                    <RateAmountCell
                      key={String(r.value)}
                      value={r.value}
                      width={80}
                      disabled={!isAdmin}
                      onSave={(v) => {
                        if (v !== null && v !== r.value) void patchRow(r.id, { value: v });
                      }}
                    />
                  ) : (
                  <input
                    className="field-input"
                    style={inputStyle(80)}
                    type="number"
                    step="any"
                    defaultValue={r.value}
                    disabled={!isAdmin}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v) && v !== r.value)
                        patchRow(r.id, { value: v });
                    }}
                  />
                  )}
                </td>
                {def.metaFields.map((f) => {
                  // one save for a meta cell, whatever types it: the field's value coerced to its
                  // declared type, dropped when empty, written only when it changed
                  const saveMeta = (raw: unknown) => {
                    const nv = coerceMeta(f.type, raw);
                    const cur =
                      r.meta && r.meta[f.name] != null
                        ? r.meta[f.name]
                        : undefined;
                    if (nv === cur) return;
                    const nextMeta = { ...(r.meta || {}) };
                    if (nv === undefined) delete nextMeta[f.name];
                    else nextMeta[f.name] = nv;
                    patchRow(r.id, {
                      meta: Object.keys(nextMeta).length ? nextMeta : null,
                    });
                  };
                  const stored = r.meta && r.meta[f.name] != null ? r.meta[f.name] : "";
                  return (
                  <td key={f.name} style={{ padding: "3px 6px" }}>
                    {def.money && f.type === "float" ? (
                      <RateAmountCell
                        key={String(stored)}
                        value={stored}
                        width={80}
                        disabled={!isAdmin}
                        onSave={(v) => {
                          saveMeta(v == null ? "" : String(v));
                        }}
                      />
                    ) : f.type === "date" ? (
                      <RateDateCell key={String(stored)} value={stored} disabled={!isAdmin} onSave={saveMeta} />
                    ) : (
                    <input
                      className="field-input"
                      style={inputStyle(80)}
                      type={f.type === "text" ? "text" : "number"}
                      step="any"
                      defaultValue={stored}
                      disabled={!isAdmin}
                      onBlur={(e) => {
                        saveMeta(e.target.value);
                      }}
                    />
                    )}
                  </td>
                  );
                })}
                <td style={{ padding: "3px 6px", textAlign: "center" }}>
                  <input
                    className="field-input"
                    style={inputStyle(48)}
                    type="number"
                    defaultValue={r.sortOrder}
                    disabled={!isAdmin}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (Number.isFinite(v) && v !== r.sortOrder)
                        patchRow(r.id, { sortOrder: v });
                    }}
                  />
                </td>
                <td style={{ padding: "3px 6px", textAlign: "center" }}>
                  <button
                    className="btn btn-ghost"
                    style={{ padding: "2px 5px" }}
                    disabled={!isAdmin}
                    onClick={() => remove(r)}
                    title={t("common.delete", { fallback: "Delete" })}
                  >
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <div
        style={{
          marginTop: 10,
          borderTop: "1px solid var(--br)",
          paddingTop: 8,
        }}
      >
        {adding ? (
          <div
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <input
              className="field-input"
              style={inputStyle(110)}
              placeholder={t("common.code", { fallback: "code" })}
              value={draft.code}
              onChange={(e) =>
                setDraft((p) => ({ ...p, code: e.target.value }))
              }
            />
            <input
              className="field-input"
              style={{ ...inputStyle(), flex: 1, minWidth: 140 }}
              placeholder={t("settings.rates.col.label", { fallback: "label" })}
              value={draft.label}
              onChange={(e) =>
                setDraft((p) => ({ ...p, label: e.target.value }))
              }
            />
            {def.money ? (
              <AmountField
                style={inputStyle(90)}
                placeholder={def.valueLabel}
                value={draft.value}
                empty=""
                inputMode="decimal"
                onAmount={(n) => {
                  setDraft((p) => ({ ...p, value: n == null ? "" : String(n) }));
                }}
              />
            ) : (
            <input
              className="field-input"
              style={inputStyle(90)}
              type="number"
              step="any"
              placeholder={def.valueLabel}
              value={draft.value}
              onChange={(e) =>
                setDraft((p) => ({ ...p, value: e.target.value }))
              }
            />
            )}
            {def.metaFields.map((f) => {
              const setMeta = (v: string) => {
                setDraft((p) => ({ ...p, meta: { ...p.meta, [f.name]: v } }));
              };
              if (def.money && f.type === "float")
                return (
                  <AmountField
                    key={f.name}
                    style={inputStyle(90)}
                    placeholder={f.name}
                    value={draft.meta[f.name] || ""}
                    empty=""
                    inputMode="decimal"
                    onAmount={(n) => {
                      setMeta(n == null ? "" : String(n));
                    }}
                  />
                );
              if (f.type === "date")
                return (
                  <DateField
                    key={f.name}
                    style={inputStyle(110)}
                    title={f.name}
                    value={draft.meta[f.name] || ""}
                    onChange={(iso) => {
                      setMeta(iso);
                    }}
                  />
                );
              return (
              <input
                key={f.name}
                className="field-input"
                style={inputStyle(90)}
                type={f.type === "text" ? "text" : "number"}
                step="any"
                placeholder={f.name}
                value={draft.meta[f.name] || ""}
                onChange={(e) =>
                  setDraft((p) => ({
                    ...p,
                    meta: { ...p.meta, [f.name]: e.target.value },
                  }))
                }
              />
              );
            })}
            <button
              className="btn btn-primary"
              style={{ padding: "2px 8px", fontSize: FS.s10 }}
              onClick={create}
              disabled={!isAdmin}
            >
              {t("common.add", { fallback: "Add" })}
            </button>
            <button
              className="btn btn-ghost"
              style={{ padding: "2px 8px", fontSize: FS.s10 }}
              onClick={() => {
                setAdding(false);
                resetDraft();
              }}
            >
              {t("common.cancel", { fallback: "Cancel" })}
            </button>
          </div>
        ) : (
          <button
            className="btn btn-ghost"
            style={{
              padding: "3px 9px",
              fontSize: FS.s10,
              display: "flex",
              alignItems: "center",
              gap: 3,
            }}
            onClick={() => setAdding(true)}
            disabled={!isAdmin}
          >
            <Plus size={12} />{" "}
            {t("settings.rates.addEntry", { fallback: "Add entry" })}
          </button>
        )}
      </div>
    </div>
  );
}

function Th({ children, style }: { children?: any; style?: CSSProperties }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: "4px 6px",
        fontSize: FS.s9,
        color: "var(--tm)",
        textTransform: "uppercase",
        letterSpacing: ".4px",
        ...(style || {}),
      }}
    >
      {children}
    </th>
  );
}

// Phase auth-c2 Stage 3 — per-card gate map. Filters RATE_KINDS by which
// gates the viewer holds. Admin overlay short-circuits inside can().
const KIND_GATE: Record<string, string> = {
  legal_interest: "settings.rates.legalInterest",
  commercial_interest: "settings.rates.commercialInterest",
  listing_fee: "settings.rates.listingFees",
  procedural_compensation: "settings.rates.proceduralCompensation",
};

type SettingsRatesProps = {
  isAdmin?: boolean;
  toast?: (msg: string) => void;
};

export default function SettingsRates({ isAdmin, toast }: SettingsRatesProps) {
  const { t } = useTranslation();
  const _perms = usePermissions();
  const _isAdminHere = !!_perms.viewer?.isAdmin;
  const canSeeKind = (kind: string) =>
    _isAdminHere || _perms.can(KIND_GATE[kind]) === true;
  const [rates, setRates] = useState<any[]>([]);
  const [coverage, setCoverage] = useState<Record<string, KindCoverage | null>>({});
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    api
      .get("/official-rates")
      .then((rows) => {
        setRates(Array.isArray(rows) ? rows : []);
        setLoaded(true);
      })
      .catch(() => {
        setRates([]);
        setLoaded(true);
      });
    // The coverage verdict, server-computed from the interest engine's own
    // period derivation. A failed fetch leaves the headline ABSENT rather than
    // showing a reassuring one: "no answer" and "covered" must not look alike.
    api
      .get("/official-rates/summary")
      .then((sum) => {
        setCoverage(
          Object.fromEntries(
            (Array.isArray(sum) ? sum : []).map((s: { kind: string; coverage: KindCoverage | null }) => [s.kind, s.coverage]),
          ),
        );
      })
      .catch(() => setCoverage({}));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      {RATE_KINDS.filter((def) => canSeeKind(def.kind)).map((def) => (
        <RateKindCard
          key={def.kind}
          def={def}
          rows={rates
            .filter((r) => r.kind === def.kind)
            .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)}
          coverage={coverage[def.kind]}
          isAdmin={isAdmin}
          toast={toast}
          onChanged={load}
          t={t}
        />
      ))}
      {!loaded && null}
    </>
  );
}
